import React, { FC } from 'react';

import {
  Button,
  Container,
  ContainerProps,
  List,
  ToggleContainer,
  ToggleContainerProps,
  useToggleContext,
} from '@lglp/components';
import {
  Image,
  MainMenuItem,
  MenuCta,
  MenuItem,
  SearchMenu,
  SubmenuItem,
} from '@lglp/prismic-client';
import { has } from 'ramda';

import { DropDownArrowIcon } from '../../../Icons';
import { LinkedComponent } from '../../../LinkedComponent';
import { HeaderLogo } from '../HeaderLogo';
import { MenuCtas } from '../MenuCtas';
import { MenuLocales } from '../MenuLocales';
import { MenuRechercher } from '../MenuRechercher';
import RegionMenu from '../RegionMenu';
import {
  MobileContext,
  MobileMenuPart,
  useInitializeContext,
  useMobileContext,
} from './MobileContext';
import styles from './MobileLayout.module.scss';
import { OpenCloseButtonMenu } from './OpenCloseMenu';

export const SubmenuContainer: FC<SubmenuContainerProps> = ({
  submenuItems,
}) => {
  const { selectSubmenuItem, displayedMenuPart, switchToMenuPart } =
    useMobileContext();

  const onClickLeftSubItem = (subItem: SubmenuItem) => {
    selectSubmenuItem(subItem);
    switchToMenuPart(MobileMenuPart.right);
  };

  return (
    <ToggleContainer.AnimatedContent>
      <ToggleContainer.Content className={styles.toggleContent}>
        <List>
          {submenuItems?.map((submenuItem, index) => (
            <List.Item
              key={index}
              className={submenuItem.isLanding && styles.landingPage}
            >
              {submenuItem.link ? (
                <LinkedComponent
                  link={submenuItem.link}
                  className={styles.link}
                >
                  {submenuItem.label}
                </LinkedComponent>
              ) : (
                <Button
                  onClick={
                    !submenuItem.link &&
                    displayedMenuPart === MobileMenuPart.left
                      ? () => onClickLeftSubItem(submenuItem)
                      : undefined
                  }
                  className={styles.link}
                >
                  <Button.Label>{submenuItem.label}</Button.Label>
                  <DropDownArrowIcon />
                </Button>
              )}
            </List.Item>
          ))}
        </List>
      </ToggleContainer.Content>
    </ToggleContainer.AnimatedContent>
  );
};

type SubmenuContainerProps = ContainerProps & {
  submenuItems: SubmenuItem[];
};

const SubmenuTrigger: FC<SubmenuTriggerProps> = ({ menuItem }) => {
  const { opened } = useToggleContext();

  return (
    <Container className={styles.items}>
      <ToggleContainer.Trigger className={styles.toggleTrigger}>
        <Container
          as="span"
          className={[styles.libelle, opened && styles.opened]}
        >
          <span className={styles.label}>{menuItem.label}</span>
        </Container>
      </ToggleContainer.Trigger>
    </Container>
  );
};

type SubmenuTriggerProps = {
  menuItem: MenuItem;
};

const hasLink = (menuItem: MenuItem) => has('link', menuItem) && menuItem.link;

const MenuContainer: FC<MenuContainerProps> = ({ menuItems, menuCtas }) => {
  return (
    <Container as="nav" className={styles.menu}>
      <Container as="ul">
        {menuItems?.map((menuItem, i) =>
          hasLink(menuItem) ? (
            <LinkedComponent key={i} link={(menuItem as SubmenuItem).link}>
              {menuItem.label}
            </LinkedComponent>
          ) : (
            <ToggleContainer as="li" key={i} className={styles.toggleContainer}>
              <SubmenuTrigger menuItem={menuItem} />
              <SubmenuContainer submenuItems={menuItem.items} />
            </ToggleContainer>
          )
        )}
        <MenuCtas menuCtas={menuCtas} className={styles.ctaMenu} />
      </Container>
    </Container>
  );
};

type MenuContainerProps = ToggleContainerProps & {
  menuItems: (MainMenuItem | SubmenuItem)[];
  menuCtas?: MenuCta[];
};

const MenuLeft: FC<MenuLeftProps> = ({
  menuItems,
  menuCtas,
  searchMenu,
  className,
}) => {
  const { displayedMenuPart } = useMobileContext();

  return (
    <Container
      className={[
        styles.left,
        displayedMenuPart === MobileMenuPart.left && styles.opened,
        className,
      ]}
    >
      <MenuContainer menuItems={menuItems} menuCtas={menuCtas} />

      <Container className={styles.regionMenuContainer}>
        <RegionMenu className={styles.regionMenu} />
      </Container>

      <Container className={styles.searchLocalsContainer}>
        <MenuLocales className={styles.localsMenu} />
        {searchMenu && (
          <MenuRechercher search={searchMenu} className={styles.searchMenu} />
        )}
      </Container>
    </Container>
  );
};

const MenuRight: FC<MenuRightProps> = ({ className }) => {
  const { selectedSubmenu, displayedMenuPart, switchToMenuPart } =
    useMobileContext();

  return (
    <Container
      className={[
        styles.right,
        displayedMenuPart === MobileMenuPart.right && styles.opened,
        className,
      ]}
    >
      <div>
        <Button
          className={styles.title}
          onClick={() => switchToMenuPart(MobileMenuPart.left)}
        >
          <Button.Label>{selectedSubmenu?.parentLabel}</Button.Label>
          <DropDownArrowIcon />
          <Button.Label className={styles.subMenu}>
            {selectedSubmenu?.label}
          </Button.Label>
        </Button>
      </div>
      <MenuContainer menuItems={selectedSubmenu?.items} />
    </Container>
  );
};

export const MobileLayout: FC<MobileLayoutProps> = ({
  menuItems,
  searchMenu,
  menuCtas,
  logo,
  className,
  ...rest
}) => {
  const providerValue = useInitializeContext();

  return (
    <MobileContext.Provider value={providerValue}>
      <Container className={[className, styles.mobileMenu]} {...rest}>
        <Container className={styles.topContainer}>
          <HeaderLogo image={logo} />
          <OpenCloseButtonMenu />
        </Container>

        {providerValue.opened && (
          <>
            <MenuLeft
              menuItems={menuItems}
              searchMenu={searchMenu}
              menuCtas={menuCtas}
              className={styles.mainContainer}
            />
            <MenuRight className={styles.mainContainer} />
          </>
        )}
      </Container>
    </MobileContext.Provider>
  );
};

type MobileLayoutProps = ContainerProps & {
  menuItems: MainMenuItem[];
  menuCtas?: MenuCta[];
  searchMenu: SearchMenu;
  logo: Image;
};

type MenuLeftProps = ContainerProps & {
  menuItems: MainMenuItem[];
  menuCtas?: MenuCta[];
  searchMenu: SearchMenu;
};
type MenuRightProps = ContainerProps;
